/**
 * Filter Diagnostics Module
 * Provides autonomous diagnostic tools for filter functionality
 */

"use strict";

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

/**
 * Generate comprehensive filter status report
 * @returns {Object} Diagnostic report
 */
export function generateFilterReport() {
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('🔍 [FilterDiagnostics] Generating comprehensive filter report...');
            }
    }
    
    const report = {
        timestamp: new Date().toISOString(),
        typeahead: {
            available: typeof $.fn.typeahead === 'function',
            isPatched: false,
            hasFilterUpdate: false,
            sourceLength: 0
        },
        filterController: {
            available: !!window.filterController,
            hasGetSelectedCategories: false,
            treesCount: 0
        },
        searchCategories: {
            available: Array.isArray(window.search_categories),
            count: window.search_categories ? window.search_categories.length : 0,
            content: window.search_categories || []
        },
        cachedRequest: {
            available: !!window.cachedRequest,
            hasFilterUpdate: false,
            hasMCond: false,
            mCondLength: 0
        },
        fileList: {
            available: !!(window.filesStore && typeof window.filesStore.getFiles==='function' && window.filesStore.getFiles().length),
            count: (window.filesStore && typeof window.filesStore.getFiles==='function') ? window.filesStore.getFiles().length : 0
        },
        ui: {
            searchBoxExists: $('#fnd_searchbox').length > 0,
            cardResultsCountExists: $('#nav-names-card-tab .card-results-count').length > 0,
            currentCount: $('#nav-names-card-tab .card-results-count').text()
        },
        functions: {
            onDropDownChange: typeof window.onDropDownChange === 'function',
            resetAccountAutocompletion: typeof window.resetAccountAutocompletion === 'function',
            addCategoryMatchConditions: typeof window.addCategoryMatchConditions === 'function',
            myEach: typeof window.myEach === 'function',
            FileListUpdateResultsCount: typeof window.FileList?.updateResultsCount === 'function'
        }
    };
    
    // Analyze typeahead
    if (typeof $.fn.typeahead === 'function') {
        const typeaheadSource = $.fn.typeahead.toString();
        report.typeahead.isPatched = typeaheadSource.includes('PATCHED');
        report.typeahead.hasFilterUpdate = typeaheadSource.includes('filterUpdate');
        report.typeahead.sourceLength = typeaheadSource.length;
    }
    
    // Analyze filter controller
    if (window.filterController) {
        report.filterController.hasGetSelectedCategories = typeof window.filterController.getSelectedCategories === 'function';
        report.filterController.treesCount = window.filterController.trees ? Object.keys(window.filterController.trees).length : 0;
    }
    
    // Analyze cached request
    if (window.cachedRequest) {
        report.cachedRequest.hasFilterUpdate = !!window.cachedRequest.filterUpdate;
        report.cachedRequest.hasMCond = !!window.cachedRequest.mCond;
        report.cachedRequest.mCondLength = window.cachedRequest.mCond ? window.cachedRequest.mCond.length : 0;
    }
    
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('📊 [FilterDiagnostics] Filter report generated:', report);
            }
    }
    return report;
}

/**
 * Test filter functionality automatically
 * @returns {Object} Test results
 */
export function testFilterFunctionality() {
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('🧪 [FilterDiagnostics] Starting automatic filter functionality test...');
            }
    }
    
    const testResults = {
        timestamp: new Date().toISOString(),
        tests: {},
        summary: {
            passed: 0,
            failed: 0,
            total: 0
        }
    };
    
    // Test 1: Typeahead availability
    testResults.tests.typeaheadAvailable = {
        name: 'Typeahead Plugin Available',
        passed: typeof $.fn.typeahead === 'function',
        details: {
            available: typeof $.fn.typeahead === 'function',
            isPatched: typeof $.fn.typeahead === 'function' ? $.fn.typeahead.toString().includes('PATCHED') : false
        }
    };
    
    // Test 2: Filter controller availability
    testResults.tests.filterControllerAvailable = {
        name: 'Filter Controller Available',
        passed: !!window.filterController,
        details: {
            available: !!window.filterController,
            hasGetSelectedCategories: window.filterController ? typeof window.filterController.getSelectedCategories === 'function' : false
        }
    };
    
    // Test 3: Search categories initialization
    testResults.tests.searchCategoriesInitialized = {
        name: 'Search Categories Initialized',
        passed: Array.isArray(window.search_categories),
        details: {
            isArray: Array.isArray(window.search_categories),
            count: window.search_categories ? window.search_categories.length : 'undefined'
        }
    };
    
    // Test 4: UI elements availability
    testResults.tests.uiElementsAvailable = {
        name: 'UI Elements Available',
        passed: $('#fnd_searchbox').length > 0 && $('#nav-names-card-tab .card-results-count').length > 0,
        details: {
            searchBoxExists: $('#fnd_searchbox').length > 0,
            counterExists: $('#nav-names-card-tab .card-results-count').length > 0
        }
    };
    
    // Test 5: Critical functions availability (updated for unified filters path)
    testResults.tests.criticalFunctionsAvailable = {
        name: 'Critical Functions Available',
        passed: typeof window.filterManager?.applyFilters === 'function' &&
                typeof window.addCategoryMatchConditions === 'function' &&
                typeof window.getMatchConditions === 'function' &&
                typeof window.myEach === 'function',
        details: {
            filterManagerApplyFilters: typeof window.filterManager?.applyFilters === 'function',
            addCategoryMatchConditions: typeof window.addCategoryMatchConditions === 'function',
            getMatchConditions: typeof window.getMatchConditions === 'function',
            myEach: typeof window.myEach === 'function'
        }
    };
    
    // Calculate summary
    for (const test of Object.values(testResults.tests)) {
        testResults.summary.total++;
        if (test.passed) {
            testResults.summary.passed++;
        } else {
            testResults.summary.failed++;
        }
    }
    
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('📊 [FilterDiagnostics] Test results:', testResults);
            }
    }
    return testResults;
}

/**
 * Validate data integrity
 * @returns {Object} Validation results
 */
export function validateDataIntegrity() {
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('🔍 [FilterDiagnostics] Validating data integrity...');
            }
    }
    
    const validation = {
        timestamp: new Date().toISOString(),
        filesList: {
            valid: false,
            issues: [],
            sampleData: null
        },
        searchCategories: {
            valid: false,
            issues: [],
            content: []
        },
        cachedRequest: {
            valid: false,
            issues: [],
            content: null
        }
    };
    
    // Validate filesList
    const __files = (window.filesStore && typeof window.filesStore.getFiles==='function') ? window.filesStore.getFiles() : [];
    if (__files && __files.length) {
        validation.filesList.valid = Array.isArray(__files) && __files.length > 0;
        
        if (validation.filesList.valid) {
            // Check first few items for data structure
            const sampleItems = __files.slice(0, 3);
            validation.filesList.sampleData = sampleItems.map(item => ({
                hasName: item && (item.n || item.name),
                hasPath: item && (item.p || item.path),
                hasSize: item && (item.s || item.size !== undefined),
                nameValue: item ? (item.n || item.name) : 'undefined',
                pathValue: item ? (item.p || item.path) : 'undefined'
            }));
            
            // Check for issues
            sampleItems.forEach((item, index) => {
                if (!item || typeof item !== 'object') {
                    validation.filesList.issues.push(`Item ${index}: not an object`);
                } else if (!item.n && !item.name) {
                    validation.filesList.issues.push(`Item ${index}: missing name field`);
                }
            });
        } else {
            validation.filesList.issues.push('filesList is not a valid array or is empty');
        }
    } else {
        validation.filesList.issues.push('filesList is not available');
    }
    
    // Validate search categories
    if (window.search_categories) {
        validation.searchCategories.valid = Array.isArray(window.search_categories);
        validation.searchCategories.content = window.search_categories;
        
        if (!validation.searchCategories.valid) {
            validation.searchCategories.issues.push('search_categories is not an array');
        }
    } else {
        validation.searchCategories.issues.push('search_categories is not available');
    }
    
    // Validate cached request
    if (window.cachedRequest) {
        validation.cachedRequest.valid = typeof window.cachedRequest === 'object';
        validation.cachedRequest.content = {
            hasFilterUpdate: !!window.cachedRequest.filterUpdate,
            hasMCond: !!window.cachedRequest.mCond,
            mCondType: typeof window.cachedRequest.mCond,
            mCondLength: window.cachedRequest.mCond ? window.cachedRequest.mCond.length : 0
        };
        
        if (!validation.cachedRequest.valid) {
            validation.cachedRequest.issues.push('cachedRequest is not an object');
        }
    } else {
        validation.cachedRequest.issues.push('cachedRequest is not available');
    }
    
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('📊 [FilterDiagnostics] Data integrity validation:', validation);
            }
    }
    return validation;
}

/**
 * Simulate filter application for testing
 * @param {string} category - Category to test (e.g., 'EN\', 'RU')
 * @returns {Promise<Object>} Simulation results
 */
export async function simulateFilterApplication(category = 'EN') {
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log(`🧪 [FilterDiagnostics] Simulating filter application for category: ${category}`);
            }
    }
    
    const simulation = {
        timestamp: new Date().toISOString(),
        category: category,
        steps: [],
        results: {}
    };
    
    try {
        // Step 1: Check if filter controller is available
        simulation.steps.push({
            step: 'Check filter controller',
            success: !!window.filterController,
            details: { available: !!window.filterController }
        });
        
        if (!window.filterController) {
            throw new Error('Filter controller not available');
        }
        
        // Step 2: Check if trees are available
        simulation.steps.push({
            step: 'Check filter trees',
            success: !!window.filterController.trees,
            details: { 
                treesAvailable: !!window.filterController.trees,
                treesCount: window.filterController.trees ? Object.keys(window.filterController.trees).length : 0
            }
        });
        
        if (!window.filterController.trees) {
            throw new Error('Filter trees not available');
        }
        
        // Step 3: Check if language tree exists
        const languageTree = window.filterController.trees.L;
        simulation.steps.push({
            step: 'Check language tree',
            success: !!languageTree,
            details: { 
                languageTreeAvailable: !!languageTree,
                treeType: languageTree ? typeof languageTree : 'undefined'
            }
        });
        
        if (!languageTree) {
            throw new Error('Language tree not available');
        }
        
        // Step 4: Select the category in the language tree (programmatically)
        simulation.steps.push({
            step: 'Select category in language tree',
            success: true,
            details: { 
                category: category,
                method: 'Programmatic selection via updateValue'
            }
        });
        
        // Programmatic selection using Treeselect API
        if (languageTree && typeof languageTree.updateValue === 'function') {
            languageTree.updateValue([category]);
        } else {
            throw new Error(`Cannot set category ${category} - Treeselect.updateValue not available`);
        }
        
        // Step 5: Wait for UI to update and get selected categories
        // Give the UI time to process the selection
        await new Promise(resolve => setTimeout(resolve, 500));
        if (debugMode === '1') {
            if (debugMode === '1') {
                console.log(`🧪 [FilterDiagnostics] Waited for UI update...`);
            }
        }
        
        const selectedCategories = window.filterController.getSelectedCategories();
        simulation.steps.push({
            step: 'Get selected categories',
            success: Array.isArray(selectedCategories),
            details: { 
                selectedCategories: selectedCategories,
                isArray: Array.isArray(selectedCategories),
                length: selectedCategories ? selectedCategories.length : 0
            }
        });
        
        // Step 6: Apply filters via unified path
        simulation.steps.push({
            step: 'Apply filters via FilterManager',
            success: typeof window.filterManager?.applyFilters === 'function',
            details: { method: 'filterManager.applyFilters' }
        });
        if (typeof window.filterManager?.applyFilters !== 'function') {
            throw new Error('filterManager.applyFilters not available');
        }
        window.filterManager.applyFilters();
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Step 7: Check UI counter element after filter application
        const counterElement = $('#nav-names-card-tab .card-results-count');
        simulation.steps.push({
            step: 'Check UI counter element after filter',
            success: counterElement.length > 0,
            details: { 
                counterExists: counterElement.length > 0,
                currentText: counterElement.text()
            }
        });
        
        simulation.results = {
            success: true,
            message: 'Filter application completed via FilterManager',
            selectedCategories: selectedCategories,
            counterAvailable: counterElement.length > 0,
            filterApplied: Array.isArray(selectedCategories) && selectedCategories.length > 0
        };
        
    } catch (error) {
        simulation.results = {
            success: false,
            error: error.message,
            step: simulation.steps.length
        };
    }
    
    if (debugMode === '1') {
        if (debugMode === '1') {
                console.log('📊 [FilterDiagnostics] Filter simulation results:', simulation);
            }
    }
    return simulation;
}

// Export all functions for global access
window.generateFilterReport = generateFilterReport;
window.testFilterFunctionality = testFilterFunctionality;
window.validateDataIntegrity = validateDataIntegrity;
window.simulateFilterApplication = simulateFilterApplication;

if (debugMode === '1') {
    if (debugMode === '1') {

            }
} 